<script>
import { Form, FormField } from '@primevue/forms';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Message from 'primevue/message';
import Popover from 'primevue/popover';
import Select from '@/Components/ShopManagement/Select.vue';
import Textarea from 'primevue/textarea';
import { queueBayhiveFormErrorNavigation } from '@/ShopManagement/formErrorNavigation.js';
import {
  formatMinutes,
  formatTime,
  timeClockClient,
} from '@/ShopManagement/timeClockClient.js';

export default {
  components: { Button, Dialog, Form, FormField, Message, Popover, Select, Textarea },

  props: {
    iconOnly: { type: Boolean, default: false },
    presentation: {
      type: String,
      default: 'popover',
      validator: (value) => ['popover', 'dialog'].includes(value),
    },
    size: {
      type: String,
      default: 'small',
      validator: (value) => value == null || ['small', 'large'].includes(value),
    },
    showTrigger: { type: Boolean, default: true },
    user: { type: Object, default: null },
  },

  emits: ['status-change'],

  data() {
    return {
      state: timeClockClient.state,
      dialogVisible: false,
      pendingAction: null,
      confirmationAction: null,
      selectedBreakTypeId: null,
      error: '',
      locationFailure: false,
      refreshTimer: null,
      failedLocationAction: null,
      failedLocationCode: null,
      exceptionReason: '',
      exceptionErrors: {},
      exceptionFormKey: 0,
      exceptionSubmitted: false,
      breakSubmitted: false,
      breakHasErrors: false,
    };
  },

  computed: {
    canUseClock() {
      return this.state.canUseClock;
    },
    enabledBreakTypes() {
      return this.state.breakTypes.filter((type) => type.enabled);
    },
    statusLabel() {
      return {
        clocked_out: 'Clocked Out',
        clocked_in: 'Clocked In',
        on_break: 'On Break',
      }[this.state.status.state] || 'Clock status';
    },
    statusSeverity() {
      // Red when stopped, yellow on break, green while the clock is counting.
      return this.state.status.state === 'clocked_out'
        ? 'danger'
        : this.state.status.state === 'on_break'
          ? 'warn'
          : 'success';
    },
    locationRequired() {
      return Boolean(this.state.settings.location.enabled);
    },
    actionLabel() {
      if (this.state.status.state === 'clocked_out') return 'Clock In';
      if (this.state.status.state === 'on_break') return 'End Break';
      return 'Clock Out';
    },
    actionIcon() {
      if (this.state.status.state === 'clocked_out') return 'fa-solid fa-right-to-bracket';
      if (this.state.status.state === 'on_break') return 'fa-solid fa-circle-play';
      return 'fa-solid fa-right-from-bracket';
    },
    todayMinutes() {
      return formatMinutes(this.state.status.todayWorkedMinutes);
    },
    manualActionLabel() {
      return this.failedLocationAction === 'clock-out' ? 'Clock Out manually' : `${this.actionLabel} manually`;
    },
    manualActionIcon() {
      return this.failedLocationAction === 'clock-out'
        ? 'fa-solid fa-right-from-bracket'
        : this.actionIcon;
    },
    canUseLocationException() {
      return ['clock-in', 'clock-out'].includes(this.failedLocationAction)
        && this.locationRequired
        && Boolean(this.state.settings.location.allowClockInExceptions);
    },
    exceptionActionLabel() {
      return this.failedLocationAction === 'clock-out'
        ? 'Clock Out with exception'
        : 'Clock In with exception';
    },
    exceptionActionVerb() {
      return this.failedLocationAction === 'clock-out' ? 'clocking out' : 'clocking in';
    },
    exceptionHasFieldErrors() {
      return Object.keys(this.exceptionErrors).some((field) => field !== 'form');
    },
    confirmationLabel() {
      return this.confirmationAction === 'start-break' ? 'Start Break' : 'Clock Out';
    },
    serviceClockWarning() {
      if (!this.confirmationAction || !this.state.status.activeServiceClock) return '';
      const action = this.confirmationAction === 'start-break' ? 'Starting a break' : 'Clocking out';
      return `${action} will stop ${this.state.status.activeServiceClock.label}.`;
    },
    overlayComponent() {
      return this.presentation === 'dialog' ? Dialog : Popover;
    },
    overlayProps() {
      if (this.presentation !== 'dialog') {
        return {
          dismissable: !this.confirmationAction,
          closeOnEscape: !this.confirmationAction,
        };
      }
      return {
        visible: this.dialogVisible,
        modal: true,
        header: 'Time Clock',
        class: 'bayhive-dialog',
        draggable: false,
        closable: !this.confirmationAction,
        closeOnEscape: !this.confirmationAction,
        dismissableMask: !this.confirmationAction,
        style: { width: 'min(22rem, calc(100vw - 2rem))' },
        contentStyle: { overflowX: 'hidden' },
      };
    },
  },

  created() {
    timeClockClient.ensureCurrentUser(this.user, this.$page?.props?.timeClock);
    timeClockClient.refreshTodayWorkedMinutes();
    this.selectedBreakTypeId = this.enabledBreakTypes[0]?.id || null;
  },

  mounted() {
    this.refreshTimer = window.setInterval(() => timeClockClient.refreshTodayWorkedMinutes(), 60000);
  },

  beforeUnmount() {
    window.clearInterval(this.refreshTimer);
  },

  watch: {
    user(value) {
      timeClockClient.ensureCurrentUser(value, this.$page?.props?.timeClock);
    },
  },

  methods: {
    formatTime,
    toggle(event) {
      if (this.presentation === 'dialog') {
        this.dialogVisible = !this.dialogVisible;
        return;
      }
      this.$refs.overlay.toggle(event);
    },
    open(event = null) {
      if (this.presentation === 'dialog') {
        this.dialogVisible = true;
        return;
      }
      this.$refs.overlay.show(event);
    },
    handleOverlayVisible(value) {
      if (this.presentation !== 'dialog') return;
      this.dialogVisible = value;
      if (!value) this.resetActionChoice();
    },
    closeOverlay() {
      if (this.presentation === 'dialog') {
        this.dialogVisible = false;
      } else {
        this.$refs.overlay?.hide();
      }
      this.resetActionChoice();
    },
    resetActionChoice() {
      this.confirmationAction = null;
      this.breakSubmitted = false;
      this.breakHasErrors = false;
    },
    requestClockOut() {
      this.error = '';
      if (!this.state.status.activeServiceClock) {
        if (this.state.status.state === 'on_break') {
          this.clockOutFromBreak(false);
          return;
        }
        this.performPrimary(false);
        return;
      }
      this.confirmationAction = 'clock-out';
    },
    requestStartBreak() {
      this.error = '';
      this.breakSubmitted = false;
      this.breakHasErrors = false;
      if (this.enabledBreakTypes.length <= 1 && !this.state.status.activeServiceClock) {
        this.startBreak();
        return;
      }
      this.confirmationAction = 'start-break';
    },
    confirmAction() {
      if (this.confirmationAction === 'start-break') {
        if (this.enabledBreakTypes.length > 1) return;
        this.startBreak();
        return;
      }
      if (this.state.status.state === 'on_break') {
        this.clockOutFromBreak(false);
        return;
      }
      this.performPrimary(false);
    },
    async getLocation() {
      if (!this.locationRequired) return null;
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        throw this.locationError('location_unavailable', 'Location is unavailable on this device.');
      }
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyMeters: position.coords.accuracy,
            capturedAt: new Date(position.timestamp).toISOString(),
          }),
          (error) => reject(this.locationError({
            1: 'location_permission_denied',
            2: 'location_unavailable',
            3: 'location_timed_out',
          }[error.code] || 'location_unavailable', {
            1: 'Location permission was denied.',
            2: 'Your location is currently unavailable.',
            3: 'Location lookup timed out.',
          }[error.code] || 'Unable to access your location.')),
          { enableHighAccuracy: true, maximumAge: 60000, timeout: 10000 },
        );
      });
    },
    locationError(code, message) {
      const error = new Error(message);
      error.code = code;
      return error;
    },
    isLocationFailure(error) {
      // The "outside allowed area" server errors do not contain the word
      // "location", so match on the known error codes rather than the message.
      const code = String(error?.code || '');
      return (
        ['location_required', 'outside_allowed_area'].includes(code) ||
        code.includes('location') ||
        /location/i.test(error?.message || '')
      );
    },
    async performPrimary(managerOverride = false, locationException = null) {
      this.error = '';
      if (!locationException) {
        this.locationFailure = false;
        this.failedLocationAction = null;
        this.failedLocationCode = null;
      }
      this.pendingAction = this.actionLabel;
      try {
        let result;
        if (this.state.status.state === 'clocked_out') {
          const location = managerOverride || locationException ? null : await this.getLocation();
          result = await timeClockClient.clockIn({ location, managerOverride, locationException });
        } else if (this.state.status.state === 'on_break') {
          result = await timeClockClient.endBreak();
        } else {
          const location = managerOverride || locationException ? null : await this.getLocation();
          result = await timeClockClient.clockOut({ location, managerOverride, locationException });
        }
        this.locationFailure = false;
        this.failedLocationAction = null;
        this.failedLocationCode = null;
        this.exceptionReason = '';
        this.exceptionErrors = {};
        this.exceptionSubmitted = false;
        this.confirmationAction = null;
        this.$emit('status-change', result.status);
        this.closeOverlay();
      } catch (error) {
        this.error = error.message || 'Unable to update the clock.';
        this.locationFailure = Boolean(locationException) || this.isLocationFailure(error);
        if (this.locationFailure) this.failedLocationAction = this.state.status.state === 'clocked_out' ? 'clock-in' : 'clock-out';
        if (this.locationFailure) this.failedLocationCode = error.code || this.failedLocationCode || 'location_unavailable';
        if (this.locationFailure && !locationException) {
          this.exceptionReason = '';
          this.exceptionErrors = {};
          this.exceptionSubmitted = false;
          this.exceptionFormKey += 1;
        }
        if (locationException) {
          const fields = error.fields || {};
          this.exceptionErrors = Object.keys(fields).length ? fields : { form: this.error };
          if (Object.keys(fields).length) {
            this.$nextTick(() => queueBayhiveFormErrorNavigation(this.$refs.exceptionForm));
          }
        }
      } finally {
        this.pendingAction = null;
      }
    },
    async startBreak(selectedTypeId = null) {
      const typeId = selectedTypeId || (this.enabledBreakTypes.length === 1
        ? this.enabledBreakTypes[0].id
        : this.enabledBreakTypes.length > 1 ? this.selectedBreakTypeId : null);
      this.error = '';
      this.pendingAction = 'Start Break';
      try {
        const result = await timeClockClient.startBreak(typeId);
        this.confirmationAction = null;
        this.$emit('status-change', result.status);
        this.closeOverlay();
      } catch (error) {
        this.error = error.message || 'Unable to start the break.';
      } finally {
        this.pendingAction = null;
      }
    },
    breakTypeResolver({ values }) {
      const errors = values.breakTypeId ? {} : { breakTypeId: [{ message: 'Break type is required.' }] };
      return { values, errors };
    },
    submitBreakChoice(event) {
      this.breakSubmitted = true;
      this.breakHasErrors = !event?.valid;
      if (!event?.valid) return;
      this.selectedBreakTypeId = event.values.breakTypeId;
      this.startBreak(event.values.breakTypeId);
    },
    exceptionValidationErrors(values = {}) {
      const reason = values.locationExceptionReason ?? this.exceptionReason;
      return String(reason || '').trim() ? {} : { locationExceptionReason: 'Reason is required.' };
    },
    exceptionResolver({ values }) {
      const errors = this.exceptionValidationErrors(values);
      return { values, errors: Object.fromEntries(Object.entries(errors).map(([field, message]) => [field, [{ message }]])) };
    },
    exceptionErrorFor(field, fieldState = null) {
      return this.exceptionErrors[field] || fieldState?.error?.message || '';
    },
    syncExceptionReason(value) {
      this.exceptionReason = value;
      this.exceptionErrors = {};
    },
    async submitLocationException(event) {
      this.exceptionSubmitted = true;
      const values = { locationExceptionReason: this.exceptionReason, ...(event?.values || {}) };
      this.exceptionErrors = this.exceptionValidationErrors(values);
      if (!event?.valid || Object.keys(this.exceptionErrors).length) return;
      const locationException = {
        reason: String(values.locationExceptionReason || '').trim(),
        failureCode: this.failedLocationCode || 'location_unavailable',
      };
      // Clocking out from an active break goes through clockOutFromBreak; every
      // other action (clock-in, or clock-out while clocked in) uses the primary.
      if (this.failedLocationAction === 'clock-out' && this.state.status.state === 'on_break') {
        await this.clockOutFromBreak(false, locationException);
      } else {
        await this.performPrimary(false, locationException);
      }
    },
    async clockOutFromBreak(managerOverride = false, locationException = null) {
      this.error = '';
      if (!locationException) {
        this.locationFailure = false;
        this.failedLocationAction = null;
      }
      this.pendingAction = 'Clock Out';
      try {
        const location = managerOverride || locationException ? null : await this.getLocation();
        const result = await timeClockClient.clockOut({ location, managerOverride, locationException });
        this.locationFailure = false;
        this.failedLocationAction = null;
        this.failedLocationCode = null;
        this.exceptionReason = '';
        this.exceptionErrors = {};
        this.exceptionSubmitted = false;
        this.confirmationAction = null;
        this.$emit('status-change', result.status);
        this.closeOverlay();
      } catch (error) {
        this.error = error.message || 'Unable to clock out.';
        this.locationFailure = Boolean(locationException) || this.isLocationFailure(error);
        if (this.locationFailure) this.failedLocationAction = 'clock-out';
        if (this.locationFailure)
          this.failedLocationCode = error.code || this.failedLocationCode || 'location_unavailable';
        if (this.locationFailure && !locationException) {
          this.exceptionReason = '';
          this.exceptionErrors = {};
          this.exceptionSubmitted = false;
          this.exceptionFormKey += 1;
        }
        if (locationException) {
          const fields = error.fields || {};
          this.exceptionErrors = Object.keys(fields).length ? fields : { form: this.error };
          if (Object.keys(fields).length) {
            this.$nextTick(() => queueBayhiveFormErrorNavigation(this.$refs.exceptionForm));
          }
        }
      } finally {
        this.pendingAction = null;
      }
    },
  },
};
</script>

<template>
  <Button
    v-if="canUseClock && showTrigger"
    type="button"
    :label="iconOnly ? undefined : statusLabel"
    icon="fa-solid fa-clock"
    :iconOnly="iconOnly"
    :severity="statusSeverity"
    :variant="state.status.state === 'clocked_out' ? 'outlined' : undefined"
    :size="size"
    class="bayhive-time-clock-trigger"
    aria-haspopup="dialog"
    :aria-label="`Time clock: ${statusLabel}`"
    @click="toggle"
  />

  <component
    v-if="canUseClock"
    :is="overlayComponent"
    ref="overlay"
    v-bind="overlayProps"
    class="bayhive-time-clock-popover"
    @update:visible="handleOverlayVisible"
    @hide="resetActionChoice"
  >
    <section
      :class="[
        'bayhive-time-clock-panel',
        { 'bayhive-time-clock-panel--dialog': presentation === 'dialog' },
      ]"
      aria-label="My time clock"
    >
      <dl class="bayhive-time-clock-summary">
        <div>
          <dt>Clocked in</dt>
          <dd>{{ state.status.clockedInAt ? formatTime(state.status.clockedInAt) : '—' }}</dd>
        </div>
        <div>
          <dt>Worked today</dt>
          <dd>{{ todayMinutes }}</dd>
        </div>
        <div v-if="state.status.activeBreak" class="bayhive-time-clock-summary-current-break">
          <dt>Current break</dt>
          <dd>{{ state.status.activeBreak.name }}</dd>
        </div>
      </dl>

      <Message v-if="serviceClockWarning" severity="warn" size="small" :closable="false">
        {{ serviceClockWarning }}
      </Message>
      <Message v-if="error" severity="error" size="small" :closable="false">{{ error }}</Message>

      <Form
        v-if="canUseLocationException"
        ref="exceptionForm"
        :key="exceptionFormKey"
        :initial-values="{ locationExceptionReason: exceptionReason }"
        :resolver="exceptionResolver"
        :validate-on-value-update="false"
        :validate-on-blur="true"
        class="bayhive-time-clock-exception-form"
        :aria-label="`${exceptionActionLabel} for a location exception`"
        @submit="submitLocationException"
      >
        <Message v-if="exceptionSubmitted && exceptionHasFieldErrors" severity="error" size="small" variant="simple">Fix the highlighted field before {{ exceptionActionVerb }}.</Message>
        <Message v-if="exceptionErrors.form" severity="error" size="small" :closable="false">{{ exceptionErrors.form }}</Message>
        <FormField v-slot="$field" name="locationExceptionReason" class="bayhive-field">
          <label for="bayhive-location-exception-reason">Exception reason <span class="line-items-required-indicator" aria-hidden="true">*</span></label>
          <Textarea
            id="bayhive-location-exception-reason"
            v-model="$field.value"
            rows="2"
            size="small"
            :invalid="Boolean(exceptionErrorFor('locationExceptionReason', $field))"
            aria-required="true"
            fluid
            @update:model-value="syncExceptionReason"
          />
          <Message v-if="exceptionErrorFor('locationExceptionReason', $field)" severity="error" size="small" variant="simple">{{ exceptionErrorFor('locationExceptionReason', $field) }}</Message>
        </FormField>
        <Button type="submit" :label="exceptionActionLabel" icon="fa-solid fa-location-dot" severity="warn" size="small" :loading="Boolean(pendingAction)" />
      </Form>

      <Form
        v-if="confirmationAction === 'start-break' && enabledBreakTypes.length > 1"
        :initial-values="{ breakTypeId: selectedBreakTypeId }"
        :resolver="breakTypeResolver"
        :validate-on-value-update="false"
        :validate-on-blur="true"
        class="bayhive-time-clock-break-select"
        aria-label="Select a break type"
        @submit="submitBreakChoice"
      >
        <Message v-if="breakSubmitted && breakHasErrors" severity="error" size="small" variant="simple">Fix the highlighted field before starting the break.</Message>
        <FormField v-slot="$field" name="breakTypeId" class="bayhive-field">
          <label for="bayhive-break-type">Break type <span class="line-items-required-indicator" aria-hidden="true">*</span></label>
          <Select
            input-id="bayhive-break-type"
            dropdown-icon="fa-solid fa-chevron-down"
            v-model="$field.value"
            :options="enabledBreakTypes"
            option-label="name"
            option-value="id"
            append-to="body"
            size="small"
            :invalid="Boolean($field.error)"
            aria-required="true"
            fluid
            @update:model-value="selectedBreakTypeId = $event"
          />
          <Message v-if="$field.error" severity="error" size="small" variant="simple">{{ $field.error.message }}</Message>
        </FormField>
        <div class="bayhive-time-clock-actions">
          <Button
            type="submit"
            label="Start Break"
            icon="fa-solid fa-mug-hot"
            severity="secondary"
            size="small"
            :loading="Boolean(pendingAction)"
          />
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            variant="text"
            size="small"
            :disabled="Boolean(pendingAction)"
            @click="closeOverlay"
          />
        </div>
      </Form>

      <div v-else-if="confirmationAction" class="bayhive-time-clock-actions">
        <Button
          :label="confirmationLabel"
          :icon="confirmationAction === 'start-break' ? 'fa-solid fa-mug-hot' : 'fa-solid fa-right-from-bracket'"
          :severity="confirmationAction === 'start-break' ? 'secondary' : 'danger'"
          size="small"
          :loading="Boolean(pendingAction)"
          @click="confirmAction"
        />
        <Button
          label="Cancel"
          severity="secondary"
          variant="text"
          size="small"
          :disabled="Boolean(pendingAction)"
          @click="closeOverlay"
        />
      </div>

      <div v-else class="bayhive-time-clock-actions">
        <Button
          v-if="state.status.state !== 'clocked_in'"
          :label="actionLabel"
          :icon="actionIcon"
          :loading="Boolean(pendingAction)"
          :severity="state.status.state === 'clocked_out' ? 'success' : 'danger'"
          size="small"
          @click="performPrimary(false)"
        />
        <Button
          v-if="state.status.state === 'clocked_in'"
          label="Clock Out"
          icon="fa-solid fa-right-from-bracket"
          severity="danger"
          size="small"
          @click="requestClockOut"
        />
        <Button
          v-if="state.status.state === 'clocked_in'"
          label="Start Break"
          icon="fa-solid fa-mug-hot"
          severity="secondary"
          size="small"
          @click="requestStartBreak"
        />
        <Button
          v-if="state.status.state === 'on_break'"
          label="Clock Out"
          icon="fa-solid fa-right-from-bracket"
          severity="danger"
          variant="outlined"
          size="small"
          :loading="pendingAction === 'Clock Out'"
          @click="requestClockOut"
        />
        <Button
          v-if="locationFailure && state.capabilities.canManage"
          :label="manualActionLabel"
          :icon="manualActionIcon"
          severity="secondary"
          variant="outlined"
          size="small"
          :loading="Boolean(pendingAction)"
          @click="failedLocationAction === 'clock-out' && state.status.state === 'on_break' ? clockOutFromBreak(true) : performPrimary(true)"
        />
      </div>
    </section>
  </component>
</template>

<style scoped>
.bayhive-time-clock-trigger { white-space: nowrap; }
.bayhive-time-clock-panel { box-sizing: border-box; display: grid; gap: var(--p-bayhive-spacing-md); max-width: 100%; min-width: 0; width: min(21rem, calc(100vw - 2rem)); }
.bayhive-time-clock-panel--dialog { width: 100%; }
.bayhive-time-clock-summary { display: grid; gap: var(--p-bayhive-spacing-sm); grid-template-columns: repeat(2, minmax(0, 1fr)); margin: 0; }
.bayhive-time-clock-summary-current-break { grid-column: 1 / -1; }
.bayhive-time-clock-summary div { background: var(--p-content-hover-background); border: 1px solid var(--p-content-border-color); border-radius: var(--p-content-border-radius); padding: var(--p-bayhive-spacing-sm); }
.bayhive-time-clock-summary dt { color: var(--p-text-muted-color); font-size: .75rem; }
.bayhive-time-clock-summary dd { font-weight: 700; margin: .15rem 0 0; }
.bayhive-time-clock-break-select { display: grid; gap: var(--p-bayhive-spacing-xs); }
.bayhive-time-clock-exception-form { display: grid; gap: var(--p-bayhive-spacing-sm); }
.bayhive-time-clock-actions { display: flex; flex-wrap: wrap; gap: var(--p-bayhive-spacing-sm); }
</style>
